/**
 * Tag Service
 *
 * Global service to look up tags (displayName, colorToken) from ListTags API data.
 * This ensures TagNodes always render with current color and name from database.
 * Tags are referenced by tagId (not by display name).
 *
 * Data Flow:
 * 1. ListTags API (useTags hook) → fetches tags with tagId, displayName, colorToken
 * 2. updateTagColorCache() → updates this service's cache (bidirectional: tagId ↔ displayName)
 * 3. TagNode.decorate() → calls getTagById(tagId) to get current data
 *
 * Cache Structure:
 * - tagByIdCache: Map<tagId, TagInfo> (O(1) lookup by tagId)
 * - tagByNameCache: Map<displayName, tagId> (O(1) lookup by displayName)
 */

import { COLOR_TOKEN_CLASSES } from "@/constants/tagColors";
import { Tag } from "@/types/proto/api/v1/tag_service_pb";

export interface TagInfo {
  displayName: string;
  colorToken: string;
}

// Primary cache: tagId → TagInfo (O(1) lookup
const tagByIdCache: Map<string, TagInfo> = new Map();

// Secondary cache: displayName → tagId (O(1) lookup for backward compatibility
const tagByNameCache: Map<string, string> = new Map();

/**
 * Get access to the internal tagByIdCache for debugging purposes
 */
export function getTagByIdCache(): Map<string, TagInfo> {
  return tagByIdCache;
}

/**
 * Get access to the internal tagByNameCache for debugging purposes
 */
export function getTagByNameCache(): Map<string, string> {
  return tagByNameCache;
}

/**
 * Update the tag cache from fresh API data
 * Call this when tagsFromAPI changes (e.g., after color update in TagManagement)
 *
 * Builds two bidirectional caches for O(1) lookup in both directions:
 * - tagByIdCache: tagId → TagInfo
 * - tagByNameCache: displayName → tagId
 *
 * @deprecated Function name refers to legacy "color" naming. Will be renamed to updateTagCache in future.
 */
export function updateTagColorCache(tags: Tag[]): void {
  tagByIdCache.clear();
  tagByNameCache.clear();

  // Build both caches for bidirectional O(1) lookup
  tags.forEach((tag) => {
    const info: TagInfo = { displayName: tag.displayName, colorToken: tag.colorToken };
    tagByIdCache.set(tag.tagId, info);
    tagByNameCache.set(tag.displayName, tag.tagId);
  });
}

/**
 * Get tag info (displayName, colorToken) by tagId
 * Called by TagNode.decorate() to get current data from database
 */
export function getTagById(tagId: string): TagInfo | null {
  return tagByIdCache.get(tagId) || null;
}

/**
 * Get color class for a tag by tagId
 */
export function getTagColorClassByTagId(tagId: string): string {
  const tagInfo = tagByIdCache.get(tagId);
  if (tagInfo) {
    const colorToken = tagInfo.colorToken;
    return COLOR_TOKEN_CLASSES[colorToken] || COLOR_TOKEN_CLASSES.primary;
  }

  return COLOR_TOKEN_CLASSES.primary;
}

/**
 * Get tagId by displayName (O(1) lookup via tagByNameCache)
 * Used by tag suggestions system
 */
export function getTagIdByDisplayName(displayName: string): string | null {
  // O(1) lookup from tagByNameCache
  return tagByNameCache.get(displayName) || null;
}

/**
 * Get color class by displayName (O(1) lookup)
 * @deprecated Use getTagColorClassByTagId instead
 */
export function getTagColorClass(displayName: string): string {
  // O(1) lookup via displayName → tagId → TagInfo
  const tagId = tagByNameCache.get(displayName);
  if (tagId) {
    const tagInfo = tagByIdCache.get(tagId);
    if (tagInfo) {
      const colorToken = tagInfo.colorToken;
      return COLOR_TOKEN_CLASSES[colorToken] || COLOR_TOKEN_CLASSES.primary;
    }
  }

  return COLOR_TOKEN_CLASSES.primary;
}
